import { ApiError } from '@/auth/api';
import type { CustomerFieldErrors, CustomerFormValues } from './types';

/**
 * The checks the backend runs on a customer (#229), with its words, so the panel marks a field
 * before the round trip. The backend runs them regardless (BR-063): this is only the early answer.
 */
export const customerFieldMessages = {
  fullNameRequired: 'Ingresá el nombre.',
  emailRequired: 'Ingresá el mail.',
  emailInvalid: 'Ingresá un mail válido, por ejemplo nombre@correo.com.',
  phoneInvalid: 'Ingresá un teléfono válido, por ejemplo 099 123 456.',
  phoneRequired: 'Una cuenta activa necesita teléfono.',
} as const;

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return emailPattern.test(value.trim());
}
const e164Pattern = /^\+[1-9]\d{7,14}$/;
const phoneSeparatorPattern = /[\s.\-()]/g;
/// Uruguay, the backend's default (`PHONE_DEFAULT_COUNTRY_CODE`).
const defaultCountryCode = '598';
const minInferredNationalDigits = 8;

/**
 * The phone as the backend stores it (E.164), or null when it cannot be one. Same four shapes:
 * `+598...`, `00598...`, `598...` and the local `099 123 456`.
 */
export function normalizePhone(value: string): string | null {
  const compact = value.trim().replace(phoneSeparatorPattern, '');
  let candidate: string;

  if (compact.startsWith('+')) {
    candidate = compact;
  } else if (compact.startsWith('00')) {
    candidate = `+${compact.slice(2)}`;
  } else if (
    compact.startsWith(defaultCountryCode) &&
    compact.length === defaultCountryCode.length + minInferredNationalDigits
  ) {
    candidate = `+${compact}`;
  } else {
    const national = compact.startsWith('0') ? compact.slice(1) : compact;

    if (national.length < minInferredNationalDigits) {
      return null;
    }

    candidate = `+${defaultCountryCode}${national}`;
  }

  return e164Pattern.test(candidate) ? candidate : null;
}

/// A stored phone as people read it: `+598 99 123 456` instead of `+59899123456`.
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) {
    return 'Sin teléfono';
  }

  const uruguayan = /^\+598(\d{2})(\d{3})(\d{3})$/.exec(phone);
  return uruguayan ? `+598 ${uruguayan[1]} ${uruguayan[2]} ${uruguayan[3]}` : phone;
}

/**
 * Every problem of the form at once. `phoneRequired` is for an active account, which cannot be
 * left without one.
 */
export function validateCustomerForm(
  values: CustomerFormValues,
  options: { phoneRequired: boolean },
): CustomerFieldErrors {
  const errors: CustomerFieldErrors = {};

  if (!values.fullName.trim()) {
    errors.fullName = customerFieldMessages.fullNameRequired;
  }

  if (!values.email.trim()) {
    errors.email = customerFieldMessages.emailRequired;
  } else if (!isValidEmail(values.email)) {
    errors.email = customerFieldMessages.emailInvalid;
  }

  if (!values.phone.trim()) {
    if (options.phoneRequired) {
      errors.phone = customerFieldMessages.phoneRequired;
    }
  } else if (!normalizePhone(values.phone)) {
    errors.phone = customerFieldMessages.phoneInvalid;
  }

  return errors;
}

/// The `fieldErrors` the backend marked, when the error carries them.
export function fieldErrorsFrom(error: unknown): CustomerFieldErrors | null {
  if (error instanceof ApiError && error.details?.fieldErrors) {
    return error.details.fieldErrors as CustomerFieldErrors;
  }

  return null;
}
